"use client";

import React, { useState, useEffect, useRef } from "react";
import type { ReactElement } from "react";
import type { GoalPlanAnalysisResponse } from "./types";
import { buildMinitoolsSpecialOfferPath } from "../../utils/minitools-url";

// Interface for the props that will be provided by the consuming app
export interface PersonalizedGoalResultPageProps {
  runId: string;
  publicAccessToken: string;
  email?: string;
  assessmentTitle?: string;
  assessmentType?: string;
  isTestMode?: boolean;
  isGated?: boolean;
  testResultsType?: "generic" | "ai";
  // Data props
  isComplete: boolean;
  analysisResult: GoalPlanAnalysisResponse | { analysis: string } | null;
  // PostHog instance provided by parent
  posthog?: {
    capture: (event: string, properties?: Record<string, unknown>) => void;
  };
  // UI Components that will be provided by the consuming app
  components: {
    Link: React.ComponentType<{ href: string; children: React.ReactNode }>;
    ContinueButton: React.ComponentType<{
      onClick: () => void;
      text: string;
      fullWidth?: boolean;
      disabled?: boolean;
      loading?: boolean;
    }>;
    LoadingSpinner?: React.ComponentType;
    EmotionalMirror?: React.ComponentType<{ analysisResult: Record<string, unknown>; className?: string }>;
    icons: {
      Target: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
      CheckCircle: React.ComponentType<{ className?: string }>;
      ChevronDown: React.ComponentType<{ className?: string }>;
      ChevronUp: React.ComponentType<{ className?: string }>;
      Trophy: React.ComponentType<{ className?: string }>;
      Calendar: React.ComponentType<{ className?: string }>;
      Clock: React.ComponentType<{ className?: string }>;
      Compass: React.ComponentType<{ className?: string }>;
      MapPin: React.ComponentType<{ className?: string }>;
      Sparkles: React.ComponentType<{ className?: string }>;
    };
  };
  // Handlers
  onContinue?: () => void;
}

/* ────────────────────────────────────────────────────────────
   LockedOverlay — frosted-glass lock card over preview content
   ──────────────────────────────────────────────────────────── */
function LockedOverlay({
  title,
  description,
  ctaText,
  onCtaClick,
  children,
  previewHeight = "200px",
}: {
  title: string;
  description: string;
  ctaText: string;
  onCtaClick: () => void;
  children: React.ReactNode;
  previewHeight?: string;
}) {
  return (
    <div className="relative">
      <div className="overflow-hidden" style={{ maxHeight: previewHeight }}>
        {children}
      </div>

      {/* Gradient fade */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, transparent 20%, rgba(249,250,251,0.6) 50%, rgba(249,250,251,0.95) 75%, rgb(249,250,251) 100%)",
        }}
      />

      {/* Lock card */}
      <div className="relative -mt-8 mx-auto max-w-md text-center bg-white rounded-xl shadow-sm p-6 z-10" style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.05)" }}>
        <div className="w-12 h-12 mx-auto mb-3 bg-amber-50 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <button
          onClick={onCtaClick}
          className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all hover:opacity-90"
          style={{ backgroundColor: "#2A6354" }}
          onMouseOver={(e) => ((e.target as HTMLButtonElement).style.backgroundColor = "#1f4a3f")}
          onMouseOut={(e) => ((e.target as HTMLButtonElement).style.backgroundColor = "#2A6354")}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
}

export function PersonalizedGoalPlanResultPage({
  runId,
  assessmentType,
  isTestMode: _isTestMode,
  isGated: _isGated = true,
  isComplete,
  analysisResult,
  posthog,
  components,
  onContinue,
}: PersonalizedGoalResultPageProps): ReactElement {

  const { Link, ContinueButton, LoadingSpinner, EmotionalMirror, icons } = components;
  const { Target, CheckCircle, ChevronDown, Clock, Sparkles } = icons;
  const [isNavigating, setIsNavigating] = useState(false);
  const hasTrackedLockedView = useRef(false);

  // Build the special offer URL
  const specialOfferHref = (() => {
    return buildMinitoolsSpecialOfferPath(assessmentType, {
      from: "assessment",
      runId,
      type: assessmentType || "",
    }, "");
  })();
  const specialOfferUrl = buildMinitoolsSpecialOfferPath(assessmentType, {
    from: "assessment",
    runId,
    type: assessmentType || "",
  });

  // Track locked section viewed (fire once)
  useEffect(() => {
    if (isComplete && analysisResult && !hasTrackedLockedView.current) {
      hasTrackedLockedView.current = true;
      if (posthog && typeof posthog.capture === "function") {
        posthog.capture("minitools_locked_section_viewed", {
          minitools_assessment_type: assessmentType,
          run_id: runId,
        });
      }
    }
  }, [isComplete, analysisResult, assessmentType, runId, posthog]);

  const handleUnlockClick = (ctaLocation: string) => {
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture("minitools_unlock_cta_clicked", {
        minitools_assessment_type: assessmentType,
        cta_location: ctaLocation,
      });
    }
    if (typeof window !== "undefined") {
      window.location.href = specialOfferUrl;
    }
  };

  const handleContinue = () => {
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture("minitools_assessment_cta_clicked", {
        minitools_assessment_type: assessmentType,
        minitools_assessment_url: typeof window !== "undefined" ? window.location.href : "",
        cta_location: "sticky_bottom",
        cta_variant: "locked_plan",
      });
    }

    setIsNavigating(true);

    if (onContinue) {
      try {
        onContinue();
      } catch (error) {
        console.error("Navigation error:", error);
        setIsNavigating(false);
      }
    }
  };

  // Show loading state if analysis is not complete
  if (!isComplete) {
    if (LoadingSpinner) {
      return <LoadingSpinner />;
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Analyzing your personalized goal plan...</p>
        </div>
      </div>
    );
  }

  // Parse goal data
  const goalData: GoalPlanAnalysisResponse = (() => {
    if (!analysisResult) {
      return {
        primaryGoal: {
          title: "Define and achieve your personalized goals",
          description: "Your personalized goal plan",
          focusArea: "Personal Growth",
        },
        subGoals: [],
        timeline: { duration: "3-month journey" },
        challenges: [],
        actionSteps: [],
      };
    }

    if ("primaryGoal" in analysisResult && typeof analysisResult.primaryGoal === "object") {
      return analysisResult as GoalPlanAnalysisResponse;
    }

    const { parseGoalPlanAnalysis } = require("./types");
    return parseGoalPlanAnalysis((analysisResult as { analysis: string }).analysis || "");
  })();

  const keySteps = goalData.actionSteps.slice(0, 3);
  const keyChallenges = goalData.challenges.slice(0, 2);

  /* ──────────────────────────────
     Gated content sections
     ────────────────────────────── */
  const actionStepsContent = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <p className="text-sm uppercase tracking-wide text-gray-500 font-semibold mb-2">First wins to celebrate</p>
      <ul className="space-y-3 text-sm text-gray-700">
        {keySteps.length > 0 ? (
          keySteps.map((step, index) => (
            <li key={index} className="flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-emerald-600 mt-1" />
              <span
                className="select-none"
                style={{ filter: "blur(5px)", color: "#9ca3af" }}
              >
                {step.step}
              </span>
            </li>
          ))
        ) : (
          <li className="text-gray-500">We'll build detailed action steps once you unlock the full plan.</li>
        )}
      </ul>
    </div>
  );

  const challengesContent = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <p className="text-sm uppercase tracking-wide text-gray-500 font-semibold mb-2">Where we'll focus coaching</p>
      <ul className="space-y-3 text-sm text-gray-700">
        {keyChallenges.length > 0 ? (
          keyChallenges.map((challenge, index) => (
            <li key={index} className="flex items-start gap-3">
              <Target className="w-4 h-4 text-emerald-600 mt-1" />
              <span
                className="select-none"
                style={{ filter: "blur(5px)", color: "#9ca3af" }}
              >
                {challenge.challenge}
              </span>
            </li>
          ))
        ) : (
          <li className="text-gray-500">Unlock the full plan to reveal personalized obstacle-breaking strategies.</li>
        )}
      </ul>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* ─── Completion Hero ─── */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Assessment Complete
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            {goalData.primaryGoal.focusArea ? (
              <>
                You've been identified as focused on{" "}
                <span className="text-xl font-bold text-emerald-700">{goalData.primaryGoal.focusArea}</span>.
                Your personalized insights are ready below.
              </>
            ) : (
              "Your personalized insights are ready below."
            )}
          </p>
          {/* Animated scroll hint */}
          <div
            style={{
              animation: "bounceDown 1.5s ease-in-out infinite",
            }}
          >
            <ChevronDown className="w-6 h-6 mx-auto text-gray-400" />
          </div>
          <style>{`
            @keyframes bounceDown {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(8px); }
            }
          `}</style>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-8 pb-6 space-y-8">
        {/* Emotional Mirror Narrative (FREE) */}
        {EmotionalMirror ? (
          <EmotionalMirror analysisResult={goalData as unknown as Record<string, unknown>} />
        ) : (
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-6 md:p-8 border border-emerald-100">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "Argent CF, serif" }}>
              Here&apos;s what we see
            </h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              You&apos;re someone focused on{" "}
              <strong className="text-emerald-700">{goalData.primaryGoal.focusArea || "personal growth"}</strong> who
              wants to <strong className="text-emerald-700">{goalData.primaryGoal.title || "achieve your goal"}</strong>.
              {keyChallenges[0]?.challenge && (
                <> The biggest thing standing in your way is <strong>{keyChallenges[0].challenge}</strong>.</>
              )}
            </p>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mt-3">
              This isn&apos;t just another goal. <strong>This matters to you.</strong> And that&apos;s exactly why
              we&apos;ve built a personalized plan around your specific situation.
            </p>
          </div>
        )}

        {/* Primary Goal Card (always visible) */}
        <div className="bg-white rounded-xl shadow-sm border border-emerald-100 p-6">
          <p className="text-sm uppercase tracking-wide text-emerald-700 font-semibold mb-2">Primary Goal</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {goalData.primaryGoal.title}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-emerald-600 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Focus area</p>
                <p className="text-sm font-medium text-gray-900">{goalData.primaryGoal.focusArea}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-emerald-600 mt-1" />
              <div>
                <p className="text-sm text-gray-500">Suggested timeline</p>
                <p className="text-sm font-medium text-gray-900">{goalData.timeline.duration}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Always gated — action steps & challenges stay locked */}
        <LockedOverlay
          title="Your Personalized Plan Is Ready"
          description="Your detailed action steps, coaching strategies, and 90-day roadmap are waiting for you."
          ctaText="Unlock Your Plan"
          onCtaClick={() => handleUnlockClick("results_section")}
          previewHeight="220px"
        >
          <div className="space-y-8">
            {actionStepsContent}
            {challengesContent}
          </div>
        </LockedOverlay>
      </div>

      {/* Sticky Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <p className="text-xs text-gray-500 text-center mb-2">
            Your detailed insights are ready to view
          </p>
          <Link href={specialOfferHref}>
            <ContinueButton
              onClick={handleContinue}
              text="Unlock Your Personalized Plan"
              fullWidth={true}
              disabled={isNavigating}
              loading={isNavigating}
            />
          </Link>
        </div>
      </div>
    </div>
  );
}
